/**
 * Create/edit form for faqItems (admin).
 */

import { useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import CustomModal from '../../shared/customModal/customModal';
import {
  Button,
  Div,
  FeedbackFormInput,
  H1,
  P,
} from '../../../styles/styles';

const LANGUAGES = ['en', 'ru', 'sr'];

export const FAQ_CREATE_FORM_ID = 'faq-item-create-form';

export function faqEditFormId(itemId) {
  return `faq-item-edit-form-${itemId}`;
}

export default function AdminFaqFormPanel({
  title,
  formId,
  formData,
  setFormData,
  isEdit,
  onCancel,
  onSubmit,
  onDeleted,
  saving,
  deleting,
}) {
  const isDesktop = useMediaQuery({ minWidth: '800px' });
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const busy = saving || deleting;

  const patchLang = (field, lang, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: { ...prev[field], [lang]: value },
    }));
  };

  const setVisibilityFlag = (field, checked) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: checked };
      if (checked) {
        if (field === 'webOnly') {
          next.mobileOnly = false;
          next.telegramOnly = false;
        }
        if (field === 'mobileOnly') {
          next.webOnly = false;
          next.telegramOnly = false;
        }
        if (field === 'telegramOnly') {
          next.webOnly = false;
          next.mobileOnly = false;
        }
      }
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  const handleDeleteConfirm = async () => {
    if (!onDeleted) return;
    await onDeleted();
    setConfirmDeleteOpen(false);
  };

  return (
    <Div
      width="100%"
      padding="20px"
      borderRadius="10px"
      backWhite
      grayBorder
      style={{ marginTop: 12, boxSizing: 'border-box' }}
    >
      <H1 fontSize="20px" margin="0 0 15px 0">
        {title}
      </H1>

      <form id={formId} onSubmit={handleSubmit} style={{ width: '100%' }}>
        <Div gap="15px" width="100%">
          {!isEdit ? (
            <Div width="100%" alignStart gap="8px">
              <P textWidth="auto" fontWeight="600" margin="0" textAlign="left">
                Document ID (optional)
              </P>
              <FeedbackFormInput
                value={formData.id}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, id: e.target.value }))
                }
                placeholder="what-is-healthy-pet"
                autoComplete="off"
                style={{ padding: '3px 5px', border: '1px gray solid' }}
              />
              <P margin="0" fontSize="12px" textAlign="left" style={{ color: '#666' }}>
                Letters, digits, hyphen, underscore. Auto-generated if empty.
              </P>
            </Div>
          ) : (
            <Div width="100%" alignStart>
              <P fontWeight="600" margin="0" textAlign="left">
                ID:
                {' '}
                {formData.id}
              </P>
            </Div>
          )}

          <Div width="100%" alignStart gap="8px">
            <P textWidth="auto" fontWeight="600" margin="0" textAlign="left">
              Order
            </P>
            <FeedbackFormInput
              type="number"
              value={formData.order === 0 ? '' : formData.order}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  order: Number(e.target.value) || 0,
                }))
              }
              placeholder="0"
              style={{ padding: '3px 5px', border: '1px gray solid', maxWidth: 120 }}
            />
          </Div>

          <Div
            width="100%"
            row={isDesktop}
            gap={isDesktop ? '12px' : '15px'}
            alignStart
            justifyStart
            wrap={!isDesktop ? 'wrap' : 'nowrap'}
          >
            {LANGUAGES.map((lang) => (
              <Div
                key={lang}
                width={isDesktop ? undefined : '100%'}
                padding="15px"
                borderRadius="8px"
                style={{
                  backgroundColor: '#f5f5f5',
                  flex: isDesktop ? '1 1 0' : undefined,
                  minWidth: isDesktop ? 0 : undefined,
                  boxSizing: 'border-box',
                }}
              >
                <P
                  fontSize="16px"
                  fontWeight="600"
                  margin="0 0 10px 0"
                  textAlign="left"
                >
                  {lang.toUpperCase()}
                </P>
                <Div gap="10px">
                  <Div>
                    <P fontSize="14px" margin="0 0 5px 0" textAlign="left">
                      Question
                    </P>
                    <FeedbackFormInput
                      value={formData.question[lang] || ''}
                      onChange={(e) => patchLang('question', lang, e.target.value)}
                      placeholder={`Question (${lang})`}
                      style={{ padding: '3px 5px', border: '1px gray solid' }}
                    />
                  </Div>
                  <Div>
                    <P fontSize="14px" margin="0 0 5px 0" textAlign="left">
                      Answer
                    </P>
                    <textarea
                      value={formData.answer[lang] || ''}
                      onChange={(e) => patchLang('answer', lang, e.target.value)}
                      placeholder={`Answer (${lang})`}
                      rows={6}
                      style={{
                        width: '100%',
                        padding: '8px 5px',
                        border: '1px gray solid',
                        borderRadius: 4,
                        fontFamily: 'inherit',
                        fontSize: 14,
                        boxSizing: 'border-box',
                        resize: 'vertical',
                      }}
                    />
                  </Div>
                </Div>
              </Div>
            ))}
          </Div>

          <Div width="100%" gap="10px" alignStart>
            <P fontWeight="600" align="left" margin="0" textWidth="auto">
              Visibility
            </P>
            <Div row gap="14px" wrap="wrap" justifyStart>
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(formData.webOnly)}
                  onChange={(e) => setVisibilityFlag('webOnly', e.target.checked)}
                />
                {' '}
                Web only
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(formData.mobileOnly)}
                  onChange={(e) => setVisibilityFlag('mobileOnly', e.target.checked)}
                />
                {' '}
                Mobile only
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(formData.telegramOnly)}
                  onChange={(e) => setVisibilityFlag('telegramOnly', e.target.checked)}
                />
                {' '}
                Telegram only
              </label>
            </Div>
          </Div>

          <Div row gap="10px" wrap="wrap" justifyStart>
            <Button type="submit" greenButton width="auto" disabled={busy}>
              {saving ? 'Saving…' : isEdit ? 'Update' : 'Create'}
            </Button>
            <Button
              type="button"
              onClick={onCancel}
              width="auto"
              outlineButton
              disabled={busy}
            >
              Cancel
            </Button>
            {isEdit && onDeleted ? (
              <Button
                type="button"
                deleteButton
                width="auto"
                disabled={busy}
                onClick={() => setConfirmDeleteOpen(true)}
              >
                Delete
              </Button>
            ) : null}
          </Div>
        </Div>
      </form>

      {isEdit && onDeleted ? (
        <CustomModal
          open={confirmDeleteOpen}
          onClose={() => !deleting && setConfirmDeleteOpen(false)}
          title="Delete FAQ item?"
        >
          <P style={{ marginBottom: 20 }}>
            This entry will be permanently removed from the FAQ list.
          </P>
          <Div row gap="10px" justifyEnd>
            <Button
              type="button"
              temporaryButton
              fontWeight="400"
              width="auto"
              disabled={deleting}
              onClick={() => setConfirmDeleteOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              deleteButton
              width="auto"
              disabled={deleting}
              onClick={handleDeleteConfirm}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </Div>
        </CustomModal>
      ) : null}
    </Div>
  );
}
